import { Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { RequestError, RequestItemAction, RequestItemError } from "domains/stock-transfer-imports/services/stock-transfer-import-service";

import ImportActionWidget from "./ImportActionWidget";
import ImportErrorAlertWidget from "./ImportErrorAlertWidget";
import localisations from "../../localisation";

export type ImportResultsProps = {
    requestErrors?: RequestError[];
    itemErrors?: RequestItemError[];
    itemActions?: RequestItemAction[];
};

export type ItemErrorsAndActions = {
    row: string;
    errors: RequestItemError[];
    actions: RequestItemAction[];
}

function DebugJson(props: { obj: any }) {
    return <pre>{`${JSON.stringify(props.obj, undefined, 2)}`}</pre>
}

function ItemActionsAndErrorsRow(props: { item: ItemErrorsAndActions }) {
    const { row, actions, errors } = props.item;
    const actionWidgets = actions.length === 0
        ? null : <Stack>{actions.map((a,i) => <ImportActionWidget key={i} action={a} />)}</Stack>;
    const errorWidgets = errors.length === 0
        ? null : <Stack spacing={1}>{errors.map((e,i)  => <ImportErrorAlertWidget key={i} error={e} />)}</Stack>;
    return ( 
        <TableRow key={row}>
            <TableCell>{row}</TableCell>
            <TableCell>
                <Stack spacing={1}>
                    {actionWidgets}
                    {errorWidgets}
                </Stack>
            </TableCell>
        </TableRow>
    );
}

function combineItemErrorsAndActions(itemErrors: RequestItemError[], itemActions: RequestItemAction[]) : ItemErrorsAndActions[] {
    const references = new Set<string>();
    itemActions.forEach(a => references.add(a.reference));
    itemErrors.forEach(e => references.add(e.reference));
    if(references.size > 0){
        const combined = Array.from(references)
                            .sort(new Intl.Collator(undefined, { numeric: true }).compare)
                            .map(r => ({
                                row: r,
                                errors: itemErrors.filter(e => e.reference === r),
                                actions: itemActions.filter(a => a.reference === r)
                            }));
        return combined;
    }
    else{
        return [];
    }
}

function getItemActionsAndErrorsColumnHeading(itemErrors: RequestItemError[], itemActions: RequestItemAction[]) : string {
    const hasPlannedActions = itemActions.find(a => a.status === "Planned");
    const hasAttemptedActions = itemActions.find(a => a.status !== "Planned");
    const hasActions = hasPlannedActions || hasAttemptedActions;
    const hasErrors = itemErrors.length > 0;
    if (hasErrors && !hasActions) return localisations.preCheckHeadings.resultTableErrors;
    if (!hasErrors && hasPlannedActions) return localisations.preCheckHeadings.resultTableActions;
    if (!hasErrors && hasAttemptedActions) return localisations.preCheckHeadings.resultTableActionsTaken;
    return localisations.preCheckHeadings.resultTableErrorsAndActions;
}

function ImportResults(props: ImportResultsProps) {
    let requestErrors : RequestError[] = props.requestErrors ?? [];
    let itemErrors : RequestItemError[] = props.itemErrors ?? [];
    let itemActions : RequestItemAction[] = props.itemActions ?? [];

    const results = combineItemErrorsAndActions(itemErrors, itemActions);
    const columnHeading = getItemActionsAndErrorsColumnHeading(itemErrors, itemActions);
    const table = results.length === 0 ? null :
    <Stack spacing={1}>
        <Typography variant="h6">
            {localisations.preCheckHeadings.itemActionsAndErrorsHeading}
        </Typography>
        <TableContainer component={Paper}>
            <Table aria-label="Import Results Table">
                <colgroup>
                <col width="1"/>
                <col />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell variant="head"><Typography variant="body1" fontWeight="bold">{localisations.preCheckHeadings.resultTableRow}</Typography></TableCell>
                        <TableCell variant="head"><Typography variant="body1" fontWeight="bold">{columnHeading}</Typography></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {results.map((r,i) => <ItemActionsAndErrorsRow key={i} item={r} /> )}
                </TableBody>
            </Table>
        </TableContainer>
    </Stack>      
    const requestTable = requestErrors.length === 0 ? null : 
        <Stack spacing={1}>
            <Typography key={0} variant="h6">
                {localisations.preCheckHeadings.fileLevelErrorsHeading}
            </Typography>
            {requestErrors.map((x, i) => <ImportErrorAlertWidget key={i+1} error={x} />)}
        </Stack>
    return (
        <>
            {requestTable}
            {table}
        </>
    );
}

export default ImportResults;
